import { useState, useEffect } from "react";
import { Container } from "@/components/Container";
import TweetCard from "@/components/TweetCard";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Persona } from "@/api/personas";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { H6 } from "@/components/ui/h6";
import YouTubeInput from "@/components/YouTubeInput";
import { LoaderCircle, Youtube, History } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  createTopicThread,
  createVideoThread,
  postThread,
  threadQueries,
} from "@/api/threads";

type ThreadType = "topic" | "video" | "previous" | null;

function ThreadsComponent({
  personaId,
  personaData,
}: {
  personaId: string;
  personaData: Persona;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // State management
  const [selectedType, setSelectedType] = useState<ThreadType>(null);
  const [topic, setTopic] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [userGuidance, setUserGuidance] = useState(""); // New state for user guidance
  const [threadLength, setThreadLength] = useState(5);
  const [isLoading, setIsLoading] = useState(false);

  // Loading state management
  const [currentLoadingStep, setCurrentLoadingStep] = useState(0);
  const [loadingMessages, setLoadingMessages] = useState<string[]>([]);
  const [loadingInterval, setLoadingInterval] = useState<NodeJS.Timeout | null>(
    null,
  );

  // Check if Twitter is connected
  const hasTwitterAuth = !!personaData?.twitter;

  // Query for previous threads
  const { data: threadsData, isLoading: isThreadsLoading } = useQuery({
    ...threadQueries.threads(personaId),
    retry: 3,
    retryDelay: 1000,
  });

  // Add useEffect to debug thread data
  useEffect(() => {
    if (threadsData) {
      //console.log("Threads data received:", threadsData);
      //console.log("Number of threads:", threadsData.data?.length);

      // Log structure of first thread if exists
      if (threadsData.data?.[0]) {
        console.log("First thread structure:", {
          doc_id: threadsData.data[0].doc_id,
          metadata: threadsData.data[0].metadata,
          tweetCount: Object.keys(threadsData.data[0].tweets || {}).length,
        });
      }
    }
  }, [threadsData, selectedType]);

  const startLoadingMessages = (messages: string[]) => {
    setLoadingMessages(messages);
    setCurrentLoadingStep(0);
    setIsLoading(true);
    const interval = setInterval(() => {
      setCurrentLoadingStep((prev) => (prev + 1) % messages.length);
    }, 5000);
    setLoadingInterval(interval);
  };

  const stopLoadingMessages = () => {
    if (loadingInterval) clearInterval(loadingInterval);
    setIsLoading(false);
    setCurrentLoadingStep(0);
    setLoadingMessages([]);
  };

  const handleCreateThread = async () => {
    if (!personaId) {
      toast({ description: "Please select an agent first." });
      return;
    }

    const loadingMessages =
      selectedType === "video"
        ? [
            "Analyzing video content...",
            "Extracting key insights...",
            "Generating thread narrative...",
            "This may take a few minutes for longer videos...",
            "Creating engaging thread structure...",
            "Finalizing thread content...",
          ]
        : [
            "Analyzing topic...",
            "Starting Agent Research...",
            "Collecting News and current events...",
            "Gathering relevant information...",
            "Structuring thread narrative...",
            "Creating engaging content...",
            "Finalizing thread...",
          ];

    try {
      startLoadingMessages(loadingMessages);

      let response;
      if (selectedType === "topic") {
        if (!topic) {
          toast({ description: "Please enter a topic." });
          return;
        }
        response = await createTopicThread(personaId, {
          topic,
          thread_length: threadLength,
        });
      } else {
        if (!videoUrl) {
          toast({ description: "Please enter a video URL." });
          return;
        }
        response = await createVideoThread(personaId, {
          youtube_url: videoUrl,
          thread_length: threadLength,
          user_guidance: userGuidance, // Include user guidance in video thread creation
        });
      }

      console.log("Thread creation response:", response);

      // Add a delay to allow Firestore to update
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Force refetch the threads data and wait for it
      await queryClient.invalidateQueries({ queryKey: ["threads", personaId] });
      const refetchResult = await queryClient.refetchQueries({
        queryKey: ["threads", personaId],
        exact: true,
      });

      console.log("Refetch result:", refetchResult);

      toast({ description: "Thread created successfully!" });

      // Reset form and switch to previous threads view to show the new thread
      setTopic("");
      setVideoUrl("");
      setUserGuidance(""); // Reset user guidance
      setSelectedType("previous");
    } catch (error: any) {
      console.error("Thread creation error:", error);
      toast({
        description:
          error.response?.data?.error ||
          "Error creating thread. Please try again.",
        variant: "destructive",
      });
    } finally {
      stopLoadingMessages();
    }
  };

  const handlePostThread = async (threadId: string) => {
    try {
      setIsLoading(true);
      await postThread(personaId, threadId);
      queryClient.invalidateQueries({ queryKey: ["threads", personaId] });
      toast({ description: "Thread posted successfully!" });
    } catch {
      toast({
        description: "Error posting thread. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      {/* Loading Dialog */}
      <Dialog open={isLoading}>
        <DialogContent className="max-w-md">
          <div className="flex flex-col items-center">
            <LoaderCircle className="h-12 w-12 animate-spin stroke-accent m-auto" />
            <p className="mt-4 text-center">
              {loadingMessages[currentLoadingStep]}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col space-y-6 max-w-4xl mx-auto">
        {/* Type Selection Buttons */}
        <div className="flex justify-center gap-4">
          <Button
            variant={selectedType === "topic" ? "default" : "outline"}
            onClick={() =>
              setSelectedType(selectedType === "topic" ? null : "topic")
            }
            className="w-40"
          >
            Topic Thread
          </Button>
          <Button
            variant={selectedType === "video" ? "default" : "outline"}
            onClick={() =>
              setSelectedType(selectedType === "video" ? null : "video")
            }
            className="w-40"
          >
            <Youtube className="mr-2 h-4 w-4" />
            Video Thread
          </Button>
          <Button
            variant={selectedType === "previous" ? "default" : "outline"}
            onClick={() =>
              setSelectedType(selectedType === "previous" ? null : "previous")
            }
            className="w-40"
          >
            <History className="mr-2 h-4 w-4" />
            Previous Threads
          </Button>
        </div>

        {/* Create Thread Form */}
        {(selectedType === "topic" || selectedType === "video") && (
          <div className="space-y-4 p-6 bg-card rounded-lg shadow">
            <H6>
              Create {selectedType === "topic" ? "Topic" : "Video"} Thread
            </H6>

            {selectedType === "topic" ? (
              <Input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Enter your topic..."
                disabled={isLoading}
              />
            ) : (
              <>
                <YouTubeInput
                  value={videoUrl}
                  onChange={setVideoUrl}
                  isLoading={isLoading}
                />
                <Input
                  value={userGuidance}
                  onChange={(e) => setUserGuidance(e.target.value)}
                  placeholder="Optional: Enter topic or guidance for the thread..."
                  disabled={isLoading}
                />
              </>
            )}

            <div className="flex items-center gap-4">
              <Label>Thread Length: {threadLength}</Label>
              <Slider
                min={3}
                max={10}
                step={1}
                value={[threadLength]}
                onValueChange={(values) => setThreadLength(values[0])}
                className="w-48"
              />
            </div>

            <Button
              onClick={handleCreateThread}
              disabled={isLoading}
              className="w-full"
            >
              Generate Thread
            </Button>
          </div>
        )}

        {/* Previous Threads Section */}
        {selectedType === "previous" && (
          <div className="space-y-4">
            <H6>Previous Threads</H6>
            {isThreadsLoading ? (
              <div className="flex justify-center">
                <LoaderCircle className="h-8 w-8 animate-spin" />
              </div>
            ) : (threadsData?.data?.length ?? 0) > 0 ? (
              <div className="space-y-4">
                {(threadsData?.data ?? []).map((thread: any) => (
                  <div
                    key={thread.doc_id}
                    className="bg-card p-4 rounded-lg shadow"
                  >
                    {/* Add metadata display */}
                    {thread.metadata && (
                      <div className="mb-4 text-sm text-muted-foreground">
                        <p>
                          Created:{" "}
                          {new Date(
                            thread.metadata.created_at,
                          ).toLocaleString()}
                        </p>
                        {thread.metadata.video_url && (
                          <p>
                            Video:{" "}
                            <a
                              href={thread.metadata.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline"
                            >
                              {thread.metadata.video_url}
                            </a>
                          </p>
                        )}
                      </div>
                    )}

                    {/* Display tweets */}
                    {Object.entries(thread.tweets)
                      .sort(([a], [b]) => Number(a) - Number(b))
                      .map(([index, tweet]: [string, any]) => (
                        <div key={index} className="mb-2">
                          <TweetCard
                            tweet={{
                              text: tweet,
                              doc_id: `${thread.doc_id}-${index}`,
                              // @ts-expect-error metadata isn't a property of tweet
                              metadata: thread.metadata,
                              images: [],
                            }}
                            personaData={personaData}
                          />
                        </div>
                      ))}

                    {/* Post button */}
                    {!thread.metadata?.posted && hasTwitterAuth && (
                      <Button
                        onClick={() => handlePostThread(thread.doc_id)}
                        className="mt-2"
                      >
                        Post to Twitter
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground">
                No previous threads found.
              </p>
            )}
          </div>
        )}
      </div>
    </Container>
  );
}

export default ThreadsComponent;
